import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon, PrismModal } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import type { OtaId } from 'frontend-utils/utils/constants/constant-values';
import HotelChannelPricingChartsChannelDiscounts from 'lumen/components/hotel/channel-pricing/charts/channel-discounts';

interface HotelChannelPricingModalsCompareChannelsPricesSignature {
  Args: {
    channelsWithDiscount: OtaId[];
    channelsWithMatchingOrHigherPrice: OtaId[];
    closeCompareChannelPricesModal: () => void;
    fromDateMoment: Dayjs;
    latestExtractDatetime: Dayjs | null;
  };
  Blocks: {
    default: [];
  };
}

export default class HotelChannelPricingModalsCompareChannelsPrices extends Component<HotelChannelPricingModalsCompareChannelsPricesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get channelsWithDiscount(): OtaId[] {
    return this.args.channelsWithDiscount || [];
  }

  get channelsWithMatchingOrHigherPrice(): OtaId[] {
    return this.args.channelsWithMatchingOrHigherPrice || [];
  }

  get discountMessageContent(): {
    text: string;
    title: string;
  } {
    const otaDiscountCount = this.channelsWithDiscount.length;

    if (otaDiscountCount > 0) {
      return {
        title: this.intl.t(
          'channel_pricing.compare_channel_prices.ota_discount_title',
          {
            count: this.channelsWithDiscount.length,
          },
        ),
        text: this.intl.t(
          'channel_pricing.compare_channel_prices.ota_discount_text',
        ),
      };
    }

    return {
      title: this.intl.t(
        'channel_pricing.compare_channel_prices.no_discount_title',
      ),
      text: this.intl.t(
        'channel_pricing.compare_channel_prices.no_discount_text',
      ),
    };
  }

  get formattedDateRange(): string {
    return this.intl.t('general.week_of', {
      date: this.args.fromDateMoment?.format('ll'),
    });
  }

  get formattedLatestExtractDatetime(): string {
    if (!this.args.latestExtractDatetime) {
      return this.intl.t('general.missing');
    }

    // Convert UTC extract datetime to hotel local time, preserving the hours
    // We cannot use toLocalTime in hotelAndMarketService because it strips the time component
    // So we take the UTC timestamp, preserve the hours, and add the timezone offset
    const extractDatetimeUTC = dayjs.utc(this.args.latestExtractDatetime);
    const timezone = this.hotelAndMarketService.timezone;
    const extractDatetimeLocal = extractDatetimeUTC
      .add(timezone, 'hour')
      .format('LLLL');

    return this.intl.t('general.updated_date', {
      date: extractDatetimeLocal,
    });
  }

  <template>
    <PrismModal @onClose={{@closeCompareChannelPricesModal}}>
      <:headerContent>
        <div class="flex w-full flex-col gap-100">
          <p class="text-200--regular text-neutral-subdued">
            {{this.formattedDateRange}}
          </p>
          <h4>{{t "channel_pricing.compare_channel_prices"}}</h4>
        </div>
      </:headerContent>
      <:default>
        <div class="modal--padded flex flex-col items-center gap-600">
          <HotelChannelPricingChartsChannelDiscounts
            @otasWithDiscount={{this.channelsWithDiscount}}
            @otasWithMatchingOrHigherPrice={{this.channelsWithMatchingOrHigherPrice}}
          />
          <div class="bg-neutral-subdued rounded-100 flex gap-200 p-200">
            <PrismIcon @name="discount-filled" @size="medium" />
            <div class="flex flex-col gap-100">
              <p class="text-200--bold">
                {{this.discountMessageContent.title}}
              </p>
              <p class="text-200--regular">
                {{this.discountMessageContent.text}}
              </p>
            </div>
          </div>
          <p class="text-200--regular text-neutral-subdued">
            {{this.formattedLatestExtractDatetime}}
          </p>
        </div>
      </:default>
    </PrismModal>
  </template>
}
